import { Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef, EventEmitter, Output, OnDestroy } from '@angular/core';
import { Subject, of } from 'rxjs';
import { debounceTime, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { BaseModel } from '../../../models/base.model';
import { EntityService } from '../../../services/entity.service';
import { GlobalModalService } from '../../../shared/global-modal/global-modal.service';
import { AuthService } from '../../../auth/auth.service';
import { LoggerService } from '../../../services/logger.service';
import { Department } from '../../../models/department.model';
import { SimilarityDetectorService, SimilarityMatch } from '../../../services/similarity-detector.service';
import { slugify } from '../../../utils/slugify';

@Component({
  selector: 'app-master-form',
  templateUrl: './master-form.component.html',
  styleUrls: ['./master-form.component.scss']
})
export class MasterFormComponent implements OnChanges, OnDestroy {
  @Input() selectedEntity: BaseModel;
  @Input() entityData: any = {};
  fields: any[] = [];
  optionsData: { [key: string]: BaseModel[] } = {};
  selectedOptions: any = {};
  formattedInputData: { [key: string]: any } = {};
  @Output() entitySaved: EventEmitter<void> = new EventEmitter<void>();
  @Output() formReset: EventEmitter<void> = new EventEmitter<void>();

  similarityState: { [fieldName: string]: {
    matches: SimilarityMatch[];
    loading: boolean;
    showPanel: boolean;
  } } = {};
  derivedValues: { [fieldName: string]: string } = {};

  private similarityInput$: { [fieldName: string]: Subject<string> } = {};
  private destroyField$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private entityService: EntityService,
    private modalService: GlobalModalService,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    private authService: AuthService,
    private similarityDetector: SimilarityDetectorService
  ) {}

  ngOnDestroy(): void {
    this.destroyField$.next();
    this.destroyField$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedEntity || changes.entityData) {
      this.initializeForm();
      this.cdr.detectChanges();
    }
  }

  canShowField(field: any): boolean {
    if (field.name === 'tenant' || field.name === 'issuperadmin') {
      return this.authService.isSuperAdmin();
    }
    return true;
  }

  initializeForm() {
    this.destroyField$.next();

    this.fields = this.entityService.getEntityFields(this.selectedEntity.getName());
    this.optionsData = {};
    this.selectedOptions = {};
    this.formattedInputData = {};
    this.similarityState = {};
    this.derivedValues = {};

    Object.values(this.similarityInput$).forEach(s => s.complete());
    this.similarityInput$ = {};

    this.fields.forEach(field => {
      if (field.type === 'select' || field.type === 'multi-choice') {
        this.loadOptions(field.entity);
        this.selectedOptions[field.name] = this.entityData[field.name] || [];
      } else if (field.type === 'date') {
        const timestamp = this.entityData[field.name];
        this.formattedInputData[field.name] = this.convertirTimestampAInputDate(timestamp);
      } else if (field.type === 'text-with-similarity') {
        this.setupSimilarityField(field);
        const initialValue = this.entityData[field.name] || '';
        this.recalculateDerivedFor(field.name, initialValue);
        if (initialValue && this.similarityInput$[field.name]) {
          this.similarityInput$[field.name].next(initialValue);
        }
      } else if (field.type === 'readonly-derived') {
        const sourceValue = this.entityData[field.derivedFrom] || '';
        this.derivedValues[field.name] = this.entityData[field.name] || this.applyTransform(field.transform, sourceValue);
      }
    });

    this.cdr.detectChanges();
  }

  private setupSimilarityField(field: any): void {
    this.similarityState[field.name] = { matches: [], loading: false, showPanel: false };

    const debounceMs = field.similarityCheck?.debounceMs ?? 400;
    const entityKey = field.similarityCheck?.entityKey;
    const excludeIdField = field.similarityCheck?.excludeIdField;

    if (!entityKey) {
      this.logger.warn(`[SIMILARITY] Campo "${field.name}" declarado text-with-similarity pero sin entityKey. Detector desactivado.`);
      return;
    }
    if (!this.similarityDetector.isRegistered(entityKey)) {
      this.logger.warn(`[SIMILARITY] entityKey "${entityKey}" no registrado en SimilarityDetectorService. Detector desactivado.`);
      return;
    }

    const subject = new Subject<string>();
    this.similarityInput$[field.name] = subject;

    subject.pipe(
      debounceTime(debounceMs),
      switchMap(value => {
        const trimmed = (value || '').trim();
        if (!trimmed) {
          return of({ similar: [] });
        }
        if (this.similarityState[field.name]) {
          this.similarityState[field.name].loading = true;
        }
        const excludeId = excludeIdField ? this.entityData[excludeIdField] : null;
        return this.similarityDetector.check(entityKey, trimmed, excludeId).pipe(
          catchError(err => {
            this.logger.warn(`[SIMILARITY] Error consultando similitud para "${field.name}":`, err?.message || err);
            return of({ similar: [] });
          })
        );
      }),
      takeUntil(this.destroyField$),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      if (!this.similarityState[field.name]) return;
      this.similarityState[field.name].loading = false;
      this.similarityState[field.name].matches = response.similar || [];
      this.similarityState[field.name].showPanel = (response.similar || []).length > 0;
      this.cdr.detectChanges();
    });
  }

  onSimilarityInputChange(fieldName: string, value: string): void {
    this.entityData[fieldName] = value;
    if (this.similarityInput$[fieldName]) {
      this.similarityInput$[fieldName].next(value);
    }
    this.recalculateDerivedFor(fieldName, value);
  }

  private recalculateDerivedFor(sourceFieldName: string, sourceValue: string): void {
    this.fields.forEach(f => {
      if (f.type === 'readonly-derived' && f.derivedFrom === sourceFieldName) {
        this.derivedValues[f.name] = this.applyTransform(f.transform, sourceValue);
      }
    });
  }

  private applyTransform(transform: string, value: string): string {
    switch (transform) {
      case 'slugify': return slugify(value);
      default:
        this.logger.warn(`[FORM] Transformación desconocida: "${transform}". Devolviendo valor sin transformar.`);
        return value || '';
    }
  }

  dismissSimilarityPanel(fieldName: string): void {
    if (this.similarityState[fieldName]) {
      this.similarityState[fieldName].showPanel = false;
      this.cdr.detectChanges();
    }
  }

  applySimilarSuggestion(fieldName: string, match: SimilarityMatch): void {
    if (!this.similarityState[fieldName]) return;
    this.entityData[fieldName] = match.name;
    this.recalculateDerivedFor(fieldName, match.name);
    this.similarityState[fieldName].showPanel = false;
    this.similarityState[fieldName].matches = [];
    this.cdr.detectChanges();
  }

  /**
   * 1c: handler del componente <app-multi-select> para multi-choice
   * interactivos (campos con `interactive: true`).
   *
   * El componente emite `selectionChange` con el array completo de objetos
   * seleccionados. Guardamos esa lista directamente en selectedOptions[fieldName]
   * para que saveEntity() la persista vía la lógica especial de campos
   * interactivos.
   */
  onMultiSelectChange(fieldName: string, items: any[]): void {
    this.selectedOptions[fieldName] = items || [];
    this.cdr.detectChanges();
  }

  loadOptions(entity: string) {
    this.logger.info("MasterFormComponent.loadOptions");
    this.entityService.getAll(entity).subscribe(
      data => {
        this.optionsData[entity] = data;
        this.markSelectedOptions(entity);
        this.cdr.detectChanges();
      },
      error => {
        this.logger.error(`loadOptions: Error al cargar los datos para el campo ${entity}`, error);
      }
    );
  }

  markSelectedOptions(entity: string) {
    this.fields.forEach(field => {
      if (field.type === 'multi-choice' && field.entity === entity) {
        const selectedValues = this.entityData[field.name] || [];
        this.selectedOptions[field.name] = this.optionsData[entity].filter(option =>
          selectedValues.some((selected: any) => selected.id === option.id)
        );
      }
    });
  }

  compareEntity(e1: Department, e2: Department): boolean {
    return e1 && e2 ? e1.id === e2.id : e1 === e2;
  }

  saveEntity() {
    const dataToSave = { ...this.entityData };

    Object.keys(this.formattedInputData).forEach(dateField => {
      const dateString = this.formattedInputData[dateField];
      if (dateString) {
        dataToSave[dateField] = new Date(dateString).getTime();
      }
    });

    this.fields.filter(f => f.type === 'readonly-derived').forEach(f => {
      delete dataToSave[f.name];
    });

    // 1c: persistencia diferenciada para campos multi-choice.
    // - Si el field es INTERACTIVO (interactive: true): enviamos un array
    //   de ids con la key `<name>Ids`. Esto coincide con el contrato que
    //   espera systemService.js -> extractTechnologyIds (acepta technologyIds).
    // - Si el field es no interactivo (comportamiento original): mantenemos
    //   la lógica antigua que escribe `<name>id` con el id del último
    //   item iterado (preserva el comportamiento existente para Sociedad
    //   .departments, Cliente.systems, etc.).
    Object.keys(this.selectedOptions).forEach(key => {
      const field = this.fields.find(f => f.name === key);
      const isInteractive = !!(field && field.interactive);

      if (Array.isArray(this.selectedOptions[key])) {
        if (isInteractive) {
          // 1c: enviar array de ids con key estándar `<name>Ids`.
          // Ejemplo: { technologyIds: ['uuid-1', 'uuid-2', 'uuid-3'] }.
          // El singular del nombre se obtiene quitando la 's' final si la
          // tiene; si no, simplemente añadimos 'Ids'. Comportamiento testeado
          // contra 'technologies' -> 'technologyIds'.
          const singular = key.endsWith('ies')
            ? key.slice(0, -3) + 'y'   // technologies -> technology
            : key.endsWith('s')
              ? key.slice(0, -1)        // systems -> system
              : key;
          dataToSave[`${singular}Ids`] = this.selectedOptions[key].map((opt: any) => opt.id);
          // Eliminamos el campo en formato 'objetos' para no enviar payload duplicado.
          delete dataToSave[key];
        } else {
          // Comportamiento original (no interactivo): pisa con el último id.
          this.selectedOptions[key].forEach((option: any) => {
            dataToSave[`${key}id`] = option.id;
          });
        }
      } else {
        dataToSave[`${key}id`] = this.selectedOptions[key].id;
      }
    });

    this.logger.log('ID:', dataToSave.id);
    this.logger.serializeMessage(dataToSave, 'Entidad guardada 1:');

    if (dataToSave.id) {
      this.entityService.update(this.selectedEntity.getApiName(), dataToSave.id, dataToSave)
        .subscribe(response => {
          this.logger.log('Entidad actualizada:', response);
          this.modalService.showSuccess('Save '+this.selectedEntity.getName(), this.selectedEntity.getName()+' saved successfully');
          this.initializeForm();
          this.entitySaved.emit();
        }, error => {
          const errorMsg = error.error?.message || error.message || 'Error desconocido';
          this.logger.error('Error al actualizar la entidad:', errorMsg);
        });
    } else {
      this.entityService.create(this.selectedEntity.getApiName(), dataToSave)
        .subscribe(response => {
          this.logger.log('Entidad creada:', response);
          this.modalService.showSuccess('Create '+this.selectedEntity.getName(), this.selectedEntity.getName()+' created successfully');
          this.initializeForm();
          this.entitySaved.emit();
        }, error => {
          const errorMsg = error.error?.message || error.message || 'Error desconocido';
          this.logger.error('Error al crear la entidad:', errorMsg);
        });
    }
  }

  deleteEntity() {
    this.modalService.confirm('Eliminar Entidad', '¿Estás seguro de que deseas eliminar esta entidad?')
      .subscribe((result: boolean) => {
        if (result) {
          this.entityService.delete(this.selectedEntity.getApiName(), this.entityData.id)
            .subscribe(response => {
              this.logger.log('Entidad eliminada:', response);
              this.modalService.showSuccess('Eliminar ' + this.selectedEntity.getName(), 'Entidad eliminada correctamente.');
              this.entitySaved.emit();
              this.formReset.emit();
            }, (error: any) => {
              this.logger.error('Error al eliminar la entidad:', error);
            });
        }
      }, (err: any) => {
        this.logger.log('Acción cancelada:', err);
      });
  }

  cancel() {
    this.formReset.emit();
    this.initializeForm();
    this.entityData = {};
    this.selectedOptions = {};
    this.fields.forEach(field => {
      this.entityData[field.name] = '';
    });
  }

  clearForm() {
    this.formReset.emit();
    this.entityData = {};
    this.selectedOptions = {};
    this.fields.forEach(field => {
      this.entityData[field.name] = '';
    });
    this.cdr.detectChanges();
  }

  private convertirTimestampAInputDate(timestamp: any): string {
    const fecha = new Date(timestamp);
    const year = fecha.getFullYear();
    const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const day = fecha.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}